import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Icon } from '@/components/synapse/Icon';
import type { ClinicalHistory } from '@/types/patients';

interface SummaryFlagsRowProps {
  history?: ClinicalHistory;
  bloodGroup?: string | null;
  /**
   * Period of amenorrhoea label (e.g. `16w 2d`) for a currently-pregnant
   * patient. When set, a green `Pregnant · <poa>` pill is shown. The panel
   * derives this from the latest valid LMP, so it is only ever passed for a
   * female patient with a recorded pregnancy.
   */
  pregnancyPoa?: string;
}

/**
 * Loud at-a-glance safety flags for the summary header: a red `Allergy ·
 * <substance>` pill per allergy (alert icon), a `Blood <group>` pill,
 * neutral/info dot-pills for chronic diseases, and a green `Pregnant · <poa>`
 * pill when the patient is currently pregnant. When the history records no
 * allergies, shows a muted "No known allergies" line instead.
 */
export function SummaryFlagsRow({ history, bloodGroup, pregnancyPoa }: SummaryFlagsRowProps) {
  const { t } = useTranslation();
  const allergies = history?.payload.allergies ?? [];
  const chronic = history?.payload.chronicDiseases ?? [];

  return (
    <div className="flex flex-wrap items-center gap-2">
      {allergies.length > 0
        ? allergies.map((a, i) => (
            <span
              key={`${a.substance}-${i}`}
              className="inline-flex h-7 items-center gap-1.5 rounded-full bg-destructive px-3 text-[12.5px] font-bold text-white"
            >
              <Icon name="alert" size={14} />
              {t('patients.allergyFlag', { substance: a.substance })}
            </span>
          ))
        : history && (
            <span className="text-[12.5px] text-muted">{t('patients.noKnownAllergies')}</span>
          )}

      {bloodGroup && (
        <Badge tone="neutral" size="lg">
          {t('patients.bloodFlag', { group: bloodGroup })}
        </Badge>
      )}

      {chronic.map((c, i) => (
        <Badge key={`${c.condition}-${i}`} tone="info" size="lg" dot>
          {c.condition}
        </Badge>
      ))}

      {pregnancyPoa && (
        <Badge tone="success" size="lg" dot>
          {t('patients.pregnantFlag', { poa: pregnancyPoa })}
        </Badge>
      )}
    </div>
  );
}
